import { el } from '../core/dom.js';
import { trapFocus } from './focus-trap.js';

export type BackfillOption =
  | { id: 'now'; label: 'Just now'; }
  | { id: 'm15'; label: '15 minutes ago'; }
  | { id: 'm30'; label: '30 minutes ago'; }
  | { id: 'h1'; label: '1 hour ago'; }
  | { id: 'h3'; label: '3 hours ago'; }
  | { id: 'yesterday'; label: 'Yesterday (this time)'; }
  | { id: 'custom'; label: 'Custom time…'; };

const OPTIONS: ReadonlyArray<{ id: string; label: string; offsetMs?: number }> = [
  { id: 'now', label: 'Just now', offsetMs: 0 },
  { id: 'm15', label: '15 minutes ago', offsetMs: 15 * 60_000 },
  { id: 'm30', label: '30 minutes ago', offsetMs: 30 * 60_000 },
  { id: 'h1', label: '1 hour ago', offsetMs: 60 * 60_000 },
  { id: 'h3', label: '3 hours ago', offsetMs: 3 * 60 * 60_000 },
  { id: 'yesterday', label: 'Yesterday (this time)', offsetMs: 24 * 60 * 60_000 },
  { id: 'custom', label: 'Custom time…' },
];

/**
 * Show a backfill picker as a bottom sheet. Returns the chosen Date, or null
 * if the user dismissed. Focus is trapped inside the sheet and restored on
 * dismiss.
 */
export async function pickBackfillTime(title = 'When did this happen?'): Promise<Date | null> {
  return new Promise((resolve) => {
    const backdrop = el('div', {
      class: 'sheet-backdrop',
      attrs: { role: 'dialog', 'aria-modal': 'true', 'aria-label': title },
    });
    const sheet = el('div', { class: 'sheet' });
    backdrop.appendChild(sheet);
    document.body.appendChild(backdrop);
    document.body.classList.add('sheet-open');
    const releaseFocus = trapFocus(sheet);

    const onKey = (e: KeyboardEvent): void => {
      if (e.key === 'Escape') done(null);
    };
    document.addEventListener('keydown', onKey);
    backdrop.addEventListener('click', (ev) => {
      if (ev.target === backdrop) done(null);
    });

    sheet.appendChild(el('div', { class: 'sheet-handle' }));
    sheet.appendChild(el('h2', {}, title));
    sheet.appendChild(el('p', { class: 'muted' }, 'Pick when it actually happened. Plan progress uses this time.'));

    const list = el('div', { class: 'stack', attrs: { style: 'gap:6px;' } });
    for (const o of OPTIONS) {
      list.appendChild(el('button', {
        class: 'btn block',
        attrs: { type: 'button' },
        onclick: () => {
          if (o.id === 'custom') { renderCustom(); return; }
          const ms = Date.now() - (o.offsetMs ?? 0);
          done(new Date(ms));
        },
      }, o.label));
    }
    sheet.appendChild(list);

    sheet.appendChild(el('button', {
      class: 'sheet-cancel', attrs: { type: 'button' },
      onclick: () => done(null),
    }, 'Cancel'));

    function renderCustom(): void {
      while (sheet.firstChild) sheet.removeChild(sheet.firstChild);
      sheet.appendChild(el('div', { class: 'sheet-handle' }));
      sheet.appendChild(el('h2', {}, 'Custom time'));
      const input = el('input', {
        type: 'datetime-local',
        value: localIsoForInput(new Date()),
      }) as HTMLInputElement;
      sheet.appendChild(el('div', { class: 'field' }, el('label', {}, 'When'), input));
      const errorLine = el('p', {
        class: 'muted-sm',
        attrs: { role: 'alert', style: 'color:var(--danger);min-height:1.25rem;margin:0.25rem 0 0;' },
      }, '');
      sheet.appendChild(errorLine);
      sheet.appendChild(el('div', { class: 'wizard-actions' },
        el('button', { class: 'btn ghost', attrs: { type: 'button' }, onclick: () => done(null) }, 'Cancel'),
        el('button', {
          class: 'btn primary', attrs: { type: 'button' },
          onclick: () => {
            const raw = input.value;
            if (!raw) { done(null); return; }
            const d = new Date(raw);
            if (Number.isNaN(d.getTime())) { done(null); return; }
            if (d.getTime() > Date.now()) {
              input.setAttribute('aria-invalid', 'true');
              errorLine.textContent = 'Pick a time in the past.';
              return;
            }
            // Reject backfills more than 30 days old — the plan engines
            // operate on recent state and very old logs are almost always
            // user error.
            const MAX_PAST_MS = 30 * 86_400_000;
            if (Date.now() - d.getTime() > MAX_PAST_MS) {
              input.setAttribute('aria-invalid', 'true');
              errorLine.textContent = 'Pick a time within the last 30 days.';
              return;
            }
            done(d);
          },
        }, 'Use this time'),
      ));
      // Refocus the new input.
      setTimeout(() => input.focus(), 20);
    }

    function done(result: Date | null): void {
      document.removeEventListener('keydown', onKey);
      releaseFocus();
      backdrop.remove();
      document.body.classList.remove('sheet-open');
      resolve(result);
    }
  });
}

function localIsoForInput(d: Date): string {
  const pad = (n: number): string => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}
